import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Alert, Button, Checkbox, Field, Input, Loader, Select } from '@itds/elements/index.js';

import H1 from '@brokerage/components/H1';

import { AgencyContext } from '@brokerage/contexts/AgencyContext';

import { Api } from '@brokerage/utils/Api';

const getRateLimitValues = data => ({
  rate_limit_replenishment_period_seconds:
    'rate_limit_replenishment_period_seconds' in data ? data.rate_limit_replenishment_period_seconds : '',
  rate_limit_token_limit: 'rate_limit_token_limit' in data ? data.rate_limit_token_limit : '',
  rate_limit_tokens_per_period: 'rate_limit_tokens_per_period' in data ? data.rate_limit_tokens_per_period : ''
});

const getMonthlyUsageValues = data => ({
  monthly_request_limit: 'monthly_request_limit' in data ? data.monthly_request_limit : ''
});

const ApiClient = () => {
  const params = useParams();

  const { member_names } = useContext(AgencyContext);

  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error_message, setErrorMessage] = useState();
  const [success_message, setSuccessMessage] = useState();

  useEffect(() => {
    setLoading(true);
    Api.loadApiClient(params.id)
      .then(response => {
        setData(response.data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  const handleChange = (name, e) => {
    setData({ ...data, [name]: e.target.value });
  };

  const handleCheck = (name, e) => {
    const checked = e.target.checked;

    if (name === 'rate_limit_enabled' && !checked) {
      setData({
        ...data,
        rate_limit_enabled: checked,
        ...getRateLimitValues(data)
      });

      return;
    }

    if (name === 'monthly_limit_enabled' && !checked) {
      setData({
        ...data,
        monthly_limit_enabled: checked,
        ...getMonthlyUsageValues(data)
      });

      return;
    }

    setData({ ...data, [name]: checked });
  };

  const handlePermissionsToggle = (name, e) => {
    const permissions = data.permissions;

    if (e.target.checked) {
      permissions.push(name);
    } else {
      permissions.splice(permissions.indexOf(name), 1);
    }

    setData({ ...data, permissions });
  };

  const handleDataSourceToggle = () => {
    setData({
      ...data,
      is_live: !data.is_live,
      is_beta: !data.is_beta
    });
  };

  const handleArchiveToggle = () => {
    setData({ ...data, is_archived: !data.is_archived });
    handleSave({ is_archived: !data.is_archived });
  };

  const rateLimitsReadOnly = !data.rate_limit_enabled || data.is_deactivated || data.is_archived;
  const monthlyUsageReadOnly =
    !data.monthly_limit_enabled || data.is_deactivated || data.is_archived;
  const sectionHeaderStyle = {
    color: '#666',
    fontSize: 13,
    fontWeight: 600,
    margin: '0 0 12px'
  };

  const handleSave = data => {
    window.scrollTo(0, 0);

    Api.updateApiClient(params.id, data)
      .then(() => {
        setSuccessMessage('Api Client updated successfully');

        setTimeout(() => {
          setSuccessMessage(null);
        }, 2000);
      })
      .catch(() => {
        setErrorMessage('Unable to update Api Client');

        setTimeout(() => {
          setErrorMessage(null);
        }, 2000);
      });
  };

  return loading ? (
    <Loader />
  ) : (
    <>
      {error_message ? <Alert type='error'>{error_message}</Alert> : null}
      {success_message ? <Alert type='success'>{success_message}</Alert> : null}

      <H1>{data.name}</H1>

      <div style={{ marginBottom: 20 }}>
        <Checkbox
          checked={data.is_deactivated}
          disabled={data.is_archived}
          label='Deactivated'
          onChange={handleCheck.bind(null, 'is_deactivated')}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Field label='App ID' style={{ width: '49%' }}>
          <Input
            onChange={handleChange.bind(null, 'app_id')}
            readOnly={true}
            type='text'
            value={data.app_id}
          />
        </Field>
        <Field label='App Token' style={{ width: '49%' }}>
          <Input
            onChange={handleChange.bind(null, 'app_token')}
            readOnly={true}
            type='text'
            value={data.app_token}
          />
        </Field>
      </div>

      <Field label='Name'>
        <Input
          onChange={handleChange.bind(null, 'name')}
          readOnly={data.is_archived}
          value={data.name}
        />
      </Field>

      <Field label='Member'>
        <Select
          onChange={handleChange.bind(null, 'member_id')}
          options={member_names}
          placeholder='-- Select Member --'
          readOnly={data.is_archived}
          value={data.member_id}
        />
      </Field>

      <Field label='Data Source'>
        <Select
          onChange={handleDataSourceToggle.bind(null)}
          options={[
            { value: 'is_live', label: 'Live' },
            { value: 'is_beta', label: 'Beta' }
          ]}
          readOnly={data.is_archived}
          value={data.is_live ? 'is_live' : 'is_beta'}
        />
      </Field>

      <Field label='Permissions'>
        <Checkbox
          checked={data.permissions ? Object.values(data.permissions).includes('quotes') : false}
          disabled={data.is_archived}
          label='Quotes'
          onChange={handlePermissionsToggle.bind(null, 'quotes')}
        />

        <Checkbox
          checked={
            data.permissions ? Object.values(data.permissions).includes('ltc_quotes') : false
          }
          disabled={data.is_archived}
          label='Long Term Care Quotes'
          onChange={handlePermissionsToggle.bind(null, 'ltc_quotes')}
        />

        <Checkbox
          checked={data.permissions ? Object.values(data.permissions).includes('forms') : false}
          disabled={data.is_archived}
          label='Forms'
          onChange={handlePermissionsToggle.bind(null, 'forms')}
        />

        <Checkbox
          checked={
            data.permissions ? Object.values(data.permissions).includes('ixn_products') : false
          }
          disabled={data.is_archived}
          label='IXN Products'
          onChange={handlePermissionsToggle.bind(null, 'ixn_products')}
        />
      </Field>

      <div style={{ marginBottom: 20 }}>
        <div style={sectionHeaderStyle}>Rate Limits</div>
        <Field label=''>
          <Checkbox
            checked={data.rate_limit_enabled || false}
            disabled={data.is_archived || data.is_deactivated}
            label='Enabled'
            onChange={handleCheck.bind(null, 'rate_limit_enabled')}
          />
        </Field>

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ width: '49%' }}>
            <Field label='Token Limit'>
              <Input
                onChange={handleChange.bind(null, 'rate_limit_token_limit')}
                readOnly={rateLimitsReadOnly}
                type='number'
                value={data.rate_limit_token_limit || ''}
              />
            </Field>
          </div>
          <div style={{ width: '49%' }}>
            <Field label='Tokens Per Period'>
              <Input
                onChange={handleChange.bind(null, 'rate_limit_tokens_per_period')}
                readOnly={rateLimitsReadOnly}
                type='number'
                value={data.rate_limit_tokens_per_period || ''}
              />
            </Field>
          </div>
        </div>

        <div style={{ width: '49%' }}>
          <Field label='Replenishment Period Seconds'>
            <Input
              onChange={handleChange.bind(null, 'rate_limit_replenishment_period_seconds')}
              readOnly={rateLimitsReadOnly}
              type='number'
              value={data.rate_limit_replenishment_period_seconds || ''}
            />
          </Field>
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={sectionHeaderStyle}>Monthly Usage</div>
        <Field label=''>
          <Checkbox
            checked={data.monthly_limit_enabled || false}
            disabled={data.is_archived || data.is_deactivated}
            label='Enabled'
            onChange={handleCheck.bind(null, 'monthly_limit_enabled')}
          />
        </Field>

        <div style={{ width: '49%' }}>
          <Field label='Monthly Request Limit'>
            <Input
              onChange={handleChange.bind(null, 'monthly_request_limit')}
              readOnly={monthlyUsageReadOnly}
              type='number'
              value={data.monthly_request_limit || ''}
            />
          </Field>
        </div>
      </div>

      <div style={{ display: 'flex' }}>
        <Button color='RED' onClick={handleArchiveToggle.bind(null)} style={{ marginRight: '5px' }}>
          {!data.is_archived ? 'Archive' : 'Unarchive'}
        </Button>
        {!data.is_archived ? (
          <Button onClick={handleSave.bind(null, data)}>Save Changes</Button>
        ) : null}
      </div>
    </>
  );
};

ApiClient.propTypes = {
  handleSave: PropTypes.func,
  handleSaveApiClient: PropTypes.func
};

export default ApiClient;
